import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Space, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';

interface TrustBarItem {
  label: string;
  desc: string;
  icon: string;
  sort_order?: number;
}

interface TrustBarEditorProps {
  data: TrustBarItem[];
  onSave: (data: TrustBarItem[]) => void;
}

const ICON_OPTIONS = [
  { label: '安全认证', value: 'SafetyCertificateOutlined' },
  { label: '机器人/AI', value: 'RobotOutlined' },
  { label: '实验/科研', value: 'ExperimentOutlined' },
  { label: '团队', value: 'TeamOutlined' },
  { label: '计算器', value: 'CalculatorOutlined' },
  { label: '折线图', value: 'LineChartOutlined' },
  { label: '药盒', value: 'MedicineBoxOutlined' },
  { label: '闪电', value: 'ThunderboltOutlined' },
];

const TrustBarEditor: React.FC<TrustBarEditorProps> = ({ data, onSave }) => {
  const [items, setItems] = useState<TrustBarItem[]>(data || []);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    setItems(data || []);
  }, [data]);

  const columns = [
    {
      title: '图标',
      dataIndex: 'icon',
      key: 'icon',
      width: 100,
    },
    {
      title: '标签',
      dataIndex: 'label',
      key: 'label',
    },
    {
      title: '描述',
      dataIndex: 'desc',
      key: 'desc',
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_: any, record: TrustBarItem, index: number) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(index)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除吗？"
            onConfirm={() => handleDelete(index)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleAdd = () => {
    setEditingIndex(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    form.setFieldsValue(items[index]);
    setModalVisible(true);
  };

  const handleDelete = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
    onSave(newItems);
    message.success('删除成功');
  };

  const handleModalOk = () => {
    form.validateFields().then((values) => {
      let newItems: TrustBarItem[];
      if (editingIndex !== null) {
        newItems = [...items];
        newItems[editingIndex] = {
          ...values,
          sort_order: items[editingIndex]?.sort_order ?? editingIndex + 1,
        };
      } else {
        newItems = [...items, { ...values, sort_order: items.length + 1 }];
      }
      setItems(newItems);
      onSave(newItems);
      setModalVisible(false);
      message.success(editingIndex !== null ? '更新成功' : '添加成功');
    });
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          添加信任指标
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={items}
        rowKey={(_, index) => String(index)}
        pagination={false}
      />

      <Modal
        title={editingIndex !== null ? '编辑信任指标' : '添加信任指标'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        width={800}
        okText="保存"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="图标"
            name="icon"
            rules={[{ required: true, message: '请选择图标' }]}
          >
            <Select placeholder="请选择图标" options={ICON_OPTIONS} />
          </Form.Item>

          <Form.Item
            label="标签"
            name="label"
            rules={[{ required: true, message: '请输入标签' }]}
          >
            <Input placeholder="例如：临床验证" />
          </Form.Item>

          <Form.Item
            label="描述"
            name="desc"
            rules={[{ required: true, message: '请输入描述' }]}
          >
            <Input placeholder="例如：1000+ 次临床验证" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TrustBarEditor;
